/**
 * PR1-D — Institution Onboarding Wizard
 * Route: /admin/institution-onboarding
 * Authorization: super_admin only
 *
 * Lifecycle: DRAFT → VALIDATION_REQUIRED → READY → ACTIVE
 *
 * Steps:
 *   1  Identity           — institution name, code, type, country
 *   2  Settings           — timezone, currency, locale
 *   3  Academic Calendar  — fiscal year, period structure
 *   4  Administrator      — initial admin account (password hashed on save)
 *   5  Integrations       — all start as NOT_CONFIGURED (informational)
 *   6  Review & Activate  — gate summary + ACTIVATE button
 *
 * INV: password submitted on step-4 save only; never stored/shown in review.
 * INV: NOT_CONFIGURED integrations shown truthfully, not as a warning.
 * INV: ACTIVATE disabled unless status = READY.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Settings, CalendarDays, UserCircle, Plug, CheckCircle2,
  XCircle, AlertTriangle, ChevronRight, ChevronLeft, Plus, Loader2,
  ShieldCheck, Info, Trash2, RefreshCw, Lock,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { PageHeader } from "../components/layout/Shell";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, key: "identity",    label: "Identity",          icon: Building2    },
  { id: 2, key: "settings",    label: "Settings",          icon: Settings     },
  { id: 3, key: "calendar",    label: "Academic Calendar", icon: CalendarDays },
  { id: 4, key: "admin",       label: "Administrator",     icon: UserCircle   },
  { id: 5, key: "integrations",label: "Integrations",      icon: Plug         },
  { id: 6, key: "package",     label: "AOS Package",       icon: ShieldCheck  },
  { id: 7, key: "review",      label: "Review & Activate", icon: ShieldCheck  },
];

const INSTITUTION_TYPES = ["UNIVERSITY","COLLEGE","POLYTECHNIC","INSTITUTE","SCHOOL","OTHER"];
const CURRENCIES = ["AED","INR","USD","GBP","EUR","SAR","QAR","SGD","KWD","BHD","OMR","EGP","PKR","MYR"];
const TIMEZONES  = [
  "Asia/Dubai","Asia/Kolkata","Asia/Riyadh","Asia/Qatar","Asia/Singapore",
  "Europe/London","America/New_York","America/Chicago","America/Los_Angeles",
  "Pacific/Auckland","Australia/Sydney","Africa/Cairo","UTC",
];
const PERIOD_STRUCTURES = ["SEMESTER","TRIMESTER","ANNUAL"];

const STATUS_COLORS = {
  DRAFT:               "bg-slate-100 text-slate-600 border-slate-200",
  VALIDATION_REQUIRED: "bg-amber-50 text-amber-700 border-amber-200",
  READY:               "bg-emerald-50 text-emerald-700 border-emerald-200",
  ACTIVATING:          "bg-blue-50 text-blue-700 border-blue-200",
  ACTIVE:              "bg-green-50 text-green-700 border-green-200",
  SUSPENDED:           "bg-red-50 text-red-700 border-red-200",
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Gate row for Review screen
// ─────────────────────────────────────────────────────────────────────────────

function GateRow({ label, state, note, variant = "normal" }) {
  const config = {
    pass:        { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50",  badge: "READY",          badgeCls: "bg-emerald-100 text-emerald-700" },
    warn:        { icon: Info,         color: "text-blue-500",    bg: "bg-blue-50",     badge: "NOT_CONFIGURED", badgeCls: "bg-blue-100 text-blue-700"     },
    fail:        { icon: XCircle,      color: "text-red-500",     bg: "bg-red-50",      badge: "MISSING",        badgeCls: "bg-red-100 text-red-700"       },
    info:        { icon: Info,         color: "text-slate-400",   bg: "bg-slate-50",    badge: "OPTIONAL",       badgeCls: "bg-slate-100 text-slate-500"   },
  }[variant] || { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", badge: "READY", badgeCls: "bg-emerald-100 text-emerald-700" };

  const Icon = config.icon;
  return (
    <div className={`flex items-center justify-between px-4 py-3 rounded-lg border border-transparent ${config.bg}`}
         data-testid={`gate-row-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-center gap-3">
        <Icon className={`w-4 h-4 flex-shrink-0 ${config.color}`} />
        <div>
          <span className="text-sm font-medium text-slate-800">{label}</span>
          {note && <p className="text-xs text-slate-500 mt-0.5">{note}</p>}
        </div>
      </div>
      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${config.badgeCls}`}>{state || config.badge}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Form input
// ─────────────────────────────────────────────────────────────────────────────

function Field({ label, required, error, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-slate-700">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500 flex items-center gap-1"><XCircle className="w-3 h-3" />{error}</p>}
    </div>
  );
}

function Input({ ...props }) {
  return (
    <input
      className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 disabled:bg-slate-50 disabled:text-slate-400 transition-colors"
      {...props}
    />
  );
}

function Select({ children, ...props }) {
  return (
    <select
      className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 disabled:bg-slate-50 disabled:text-slate-400 transition-colors"
      {...props}
    >
      {children}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step components
// ─────────────────────────────────────────────────────────────────────────────

function StepIdentity({ data, onChange, errors, disabled }) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">Define the institution's legal identity and classification.</p>
      <Field label="Full Legal Name" required error={errors?.name}>
        <Input data-testid="identity-name" value={data.name || ""} disabled={disabled}
               onChange={e => onChange("name", e.target.value)} placeholder="e.g. King Abdullah University of Science and Technology" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Short Code" required error={errors?.short_code}>
          <Input data-testid="identity-short-code" value={data.short_code || ""} disabled={disabled}
                 onChange={e => onChange("short_code", e.target.value.toUpperCase())}
                 placeholder="e.g. KAUST" maxLength={12} />
          <p className="text-xs text-slate-400">Unique 2–12 char code. Cannot be changed after activation.</p>
        </Field>
        <Field label="Country (ISO 2)" required error={errors?.country_code}>
          <Input data-testid="identity-country" value={data.country_code || ""} disabled={disabled}
                 onChange={e => onChange("country_code", e.target.value.toUpperCase())}
                 placeholder="e.g. AE" maxLength={2} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Institution Type" required>
          <Select data-testid="identity-type" value={data.institution_type || "UNIVERSITY"} disabled={disabled}
                  onChange={e => onChange("institution_type", e.target.value)}>
            {INSTITUTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="Website">
          <Input data-testid="identity-website" value={data.website || ""} disabled={disabled}
                 onChange={e => onChange("website", e.target.value)} placeholder="https://..." />
        </Field>
      </div>
    </div>
  );
}

function StepSettings({ data, onChange, errors, disabled }) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">Configure locale, currency, and timezone defaults for the institution.</p>
      <Field label="Timezone" required error={errors?.timezone}>
        <Select data-testid="settings-timezone" value={data.timezone || ""} disabled={disabled}
                onChange={e => onChange("timezone", e.target.value)}>
          <option value="">Select timezone…</option>
          {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Currency (ISO 4217)" required error={errors?.currency_code}>
          <Select data-testid="settings-currency" value={data.currency_code || ""} disabled={disabled}
                  onChange={e => onChange("currency_code", e.target.value)}>
            <option value="">Select currency…</option>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
        </Field>
        <Field label="Locale">
          <Input data-testid="settings-locale" value={data.locale || "en"} disabled={disabled}
                 onChange={e => onChange("locale", e.target.value)} placeholder="en" />
        </Field>
      </div>
      <Field label="Date Format">
        <Select data-testid="settings-date-format" value={data.date_format || "DD/MM/YYYY"} disabled={disabled}
                onChange={e => onChange("date_format", e.target.value)}>
          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
        </Select>
      </Field>
    </div>
  );
}

function StepCalendar({ data, onChange, errors, disabled }) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">Set the academic year structure and fiscal calendar boundaries.</p>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Fiscal Year Start Month" required error={errors?.fiscal_year_start_month}>
          <Select data-testid="calendar-fy-start" value={data.fiscal_year_start_month || ""} disabled={disabled}
                  onChange={e => onChange("fiscal_year_start_month", parseInt(e.target.value))}>
            <option value="">Select month…</option>
            {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m,i) =>
              <option key={i+1} value={i+1}>{m} ({String(i+1).padStart(2,"0")})</option>
            )}
          </Select>
        </Field>
        <Field label="Period Structure" required>
          <Select data-testid="calendar-period" value={data.period_structure || "SEMESTER"} disabled={disabled}
                  onChange={e => onChange("period_structure", e.target.value)}>
            {PERIOD_STRUCTURES.map(p => <option key={p} value={p}>{p}</option>)}
          </Select>
        </Field>
      </div>
      <Field label="First Academic Year" required error={errors?.first_academic_year}>
        <Input data-testid="calendar-year" type="number" min={2020} max={2040}
               value={data.first_academic_year || ""} disabled={disabled}
               onChange={e => onChange("first_academic_year", parseInt(e.target.value))}
               placeholder="e.g. 2026" />
        <p className="text-xs text-slate-400">The opening fiscal year provisioned at activation (2020–2040).</p>
      </Field>
    </div>
  );
}

function StepAdmin({ data, onChange, errors, disabled, isNew }) {
  const [showPw, setShowPw] = useState(false);
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        Provision the initial Institution Administrator account.
        The password is hashed immediately on save and is never stored in plaintext.
      </p>
      {data.password_set && !isNew && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">
          <Lock className="w-4 h-4" /> Password is set and secured. Enter a new value only to change it.
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Full Name" required error={errors?.name}>
          <Input data-testid="admin-name" value={data.name || ""} disabled={disabled}
                 onChange={e => onChange("name", e.target.value)} placeholder="Full legal name" />
        </Field>
        <Field label="Title">
          <Input data-testid="admin-title" value={data.title || ""} disabled={disabled}
                 onChange={e => onChange("title", e.target.value)} placeholder="e.g. Vice-Chancellor" />
        </Field>
      </div>
      <Field label="Email Address" required error={errors?.email}>
        <Input data-testid="admin-email" type="email" value={data.email || ""} disabled={disabled}
               onChange={e => onChange("email", e.target.value)} placeholder="admin@institution.edu" />
      </Field>
      <Field label="Initial Password" required={!data.password_set} error={errors?.initial_password}>
        <div className="relative">
          <Input data-testid="admin-password" type={showPw ? "text" : "password"}
                 value={data._password || ""} disabled={disabled}
                 onChange={e => onChange("_password", e.target.value)}
                 placeholder={data.password_set ? "Leave blank to keep existing" : "Minimum 8 characters"} />
          <button type="button" onClick={() => setShowPw(s => !s)}
                  className="absolute right-3 top-2.5 text-xs text-slate-400 hover:text-slate-600 transition-colors">
            {showPw ? "Hide" : "Show"}
          </button>
        </div>
        <p className="text-xs text-slate-400">Min. 8 chars. Plaintext is discarded after hashing on save.</p>
      </Field>
    </div>
  );
}

function StepIntegrations({ data }) {
  const rows = [
    { key: "identity_provider",   label: "Identity Provider (SSO / LDAP)" },
    { key: "ai_provider",         label: "AI Provider" },
    { key: "erp_connector",       label: "ERP Connector" },
    { key: "notification_provider", label: "Notification Provider" },
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-800">Integrations start as NOT_CONFIGURED</p>
          <p className="text-sm text-blue-600 mt-0.5">
            This is the correct, expected state for a new institution. External integrations are
            provisioned after activation via the Integrations workspace. This does not block activation.
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map(r => (
          <div key={r.key} data-testid={`integration-row-${r.key}`}
               className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 border border-slate-200">
            <span className="text-sm text-slate-700">{r.label}</span>
            <span className="text-xs font-semibold bg-slate-200 text-slate-600 px-2.5 py-1 rounded-full">
              {data[r.key] || "NOT_CONFIGURED"}
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 px-1">
        PR1-D control D-03: No integration is active unless the institution explicitly configures it.
        These values will remain NOT_CONFIGURED until changed via Administration → Integrations.
      </p>
    </div>
  );
}

// SUB-01-E: Package Selection Step
function StepPackage({ data, onChange, loading: pkgLoading, packages, error }) {
  const selected = data.package_code || "";
  const count = parseInt(data.enrolled_student_count, 10) || 0;

  // Find active band for the selected package based on student count
  const selectedPkg = packages.find(p => p.package_code === selected);
  const activeBand = selectedPkg?.bands
    ? selectedPkg.bands.find(b =>
        count >= (b.min_enrolled || 0) &&
        (b.max_enrolled == null || count <= b.max_enrolled)
      ) || selectedPkg.bands[selectedPkg.bands.length - 1]
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200">
        <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-emerald-800">AOS Package Selection (Optional)</p>
          <p className="text-sm text-emerald-700 mt-0.5">
            Selecting a package activates a subscription and provisions module entitlements for this
            institution. You can skip and assign after activation. No billing is activated.
          </p>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {pkgLoading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm px-1">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading packages…
        </div>
      ) : (
        <div className="space-y-3">
          {/* Skip option */}
          <label
            data-testid="package-option-skip"
            className={`flex items-center gap-3 px-4 py-4 rounded-lg border-2 cursor-pointer transition-all ${
              selected === ""
                ? "border-slate-400 bg-slate-50"
                : "border-slate-200 bg-white hover:border-slate-300"
            }`}
          >
            <input
              type="radio"
              name="package_code"
              value=""
              checked={selected === ""}
              onChange={() => onChange({ ...data, package_code: "", edition_code: "" })}
              className="w-4 h-4 text-slate-600"
            />
            <div>
              <p className="text-sm font-semibold text-slate-700">Skip — assign package later</p>
              <p className="text-xs text-slate-500">Institution activates without a subscription or entitlements</p>
            </div>
          </label>

          {packages.map(pkg => (
            <label
              key={pkg.package_code}
              data-testid={`package-option-${pkg.package_code}`}
              className={`flex items-start gap-3 px-4 py-4 rounded-lg border-2 cursor-pointer transition-all ${
                selected === pkg.package_code
                  ? "border-emerald-500 bg-emerald-50"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <input
                type="radio"
                name="package_code"
                value={pkg.package_code}
                checked={selected === pkg.package_code}
                onChange={() => onChange({ ...data, package_code: pkg.package_code, edition_code: pkg.edition_code })}
                className="w-4 h-4 text-emerald-600 mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-slate-800">{pkg.package_name}</p>
                  <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                    {pkg.edition_code}
                  </span>
                  {pkg.modules_count > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {pkg.modules_count} modules
                    </span>
                  )}
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                    {pkg.billing_model}
                  </span>
                </div>
                {pkg.description && (
                  <p className="text-xs text-slate-500 mt-1">{pkg.description}</p>
                )}
                <p className="text-xs text-slate-400 mt-1">
                  {pkg.bands?.length || 0} enrollment bands · No pricing activated
                </p>
              </div>
            </label>
          ))}
        </div>
      )}

      {selected && (
        <div className="space-y-3 pt-2 border-t border-slate-200">
          <p className="text-sm font-medium text-slate-700">Enrolled student count</p>
          <input
            type="number"
            min={0}
            value={data.enrolled_student_count || 0}
            onChange={e => onChange({ ...data, enrolled_student_count: parseInt(e.target.value, 10) || 0 })}
            placeholder="Current enrolled students"
            data-testid="package-enrolled-count"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          />
          {activeBand && count > 0 && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-sm"
              data-testid="active-band-indicator"
            >
              <span className="font-medium text-blue-800">Active band:</span>
              <span className="text-blue-700">
                {activeBand.band_name} ({activeBand.min_enrolled.toLocaleString()}–
                {activeBand.max_enrolled != null
                  ? activeBand.max_enrolled.toLocaleString()
                  : "∞"} students)
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StepReview({ record, onValidate, onActivate, validating, activating, activationResult }) {
  const sc = record.steps_complete || {};
  const errs = record.validation_errors || [];
  const pkg = record.step_package || {};

  const isReady = record.status === "READY";
  const isActive = record.status === "ACTIVE";
  const isActivating = record.status === "ACTIVATING";

  // Package gate display
  const pkgSelected = !!pkg.package_code;
  const pkgNote = pkgSelected
    ? `${pkg.package_code} · ${pkg.edition_code || ""} edition${
        pkg.enrolled_student_count > 0 ? ` · ${pkg.enrolled_student_count.toLocaleString()} students` : ""
      }`
    : "No package selected — subscription will be assigned after activation";

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        Review all configuration gates before activating the institution.
        Activation provisions accounts in both PostgreSQL and MongoDB.
        {pkgSelected && " Package activation will provision module entitlements."}
      </p>

      {/* Activation gates */}
      <div className="space-y-2" data-testid="activation-gates">
        <GateRow label="Institution Identity" state="READY" variant={sc.identity ? "pass" : "fail"}
                 note={sc.identity ? record.step_identity?.name : "Name, code, and country required"} />
        <GateRow label="Settings" state="READY" variant={sc.settings ? "pass" : "fail"}
                 note={sc.settings ? `${record.step_settings?.currency_code} · ${record.step_settings?.timezone}` : "Timezone and currency required"} />
        <GateRow label="Academic Calendar" state="READY" variant={sc.calendar ? "pass" : "fail"}
                 note={sc.calendar ? `FY starts month ${record.step_calendar?.fiscal_year_start_month} · ${record.step_calendar?.period_structure}` : "Fiscal year config required"} />
        <GateRow label="Initial Administrator" state={record.step_admin?.password_set ? "SECURED" : "MISSING"} variant={sc.admin ? "pass" : "fail"}
                 note={sc.admin ? record.step_admin?.email : "Administrator name, email, and password required"} />
        <GateRow label="Integrations" state="NOT_CONFIGURED" variant="warn"
                 note="All external integrations default to NOT_CONFIGURED. Activation proceeds." />
        {/* SUB-02: AOS Package gate row */}
        <GateRow
          label="AOS Package"
          state={pkgSelected ? "SELECTED" : "SKIP"}
          variant={pkgSelected ? "pass" : "info"}
          note={pkgNote}
          data-testid="package-gate-row"
        />
        <GateRow label="Reference Data" state="CLEAN" variant="pass"
                 note="No demo or reference data will be promoted into this institution (PR1-D gate D-06)." />
        <GateRow label="Production Validation" state={isReady || isActive ? "PASS" : "PENDING"}
                 variant={isReady || isActive ? "pass" : "info"}
                 note={isReady || isActive ? "All mandatory fields validated." : "Run validation to check."} />
      </div>

      {/* Validation errors */}
      {errs.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2" data-testid="validation-errors-panel">
          <p className="text-sm font-semibold text-red-700 flex items-center gap-2">
            <XCircle className="w-4 h-4" /> {errs.length} validation error{errs.length > 1 ? "s" : ""} must be resolved
          </p>
          {errs.map((e, i) => (
            <p key={i} className="text-xs text-red-600">Step {e.step}: {e.field} — {e.message}</p>
          ))}
        </div>
      )}

      {/* Status badge */}
      <div className="flex items-center gap-3" data-testid="onboarding-status-row">
        <span className="text-sm text-slate-500">Current status:</span>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${STATUS_COLORS[record.status] || ""}`}
              data-testid="onboarding-status-badge">
          {record.status}
        </span>
        {isActivating && (
          <span className="text-xs text-blue-600 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Retry safe — pre-committed IDs in use
          </span>
        )}
      </div>

      {/* Action row */}
      <div className="flex items-center gap-3 pt-2">
        {!isActive && !isActivating && (
          <Button variant="outline" size="sm" onClick={onValidate} disabled={validating}
                  data-testid="validate-btn" className="gap-2">
            {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Run Validation
          </Button>
        )}
        {(isReady || isActivating) && !isActive && (
          <Button size="sm" onClick={onActivate} disabled={activating}
                  data-testid="activate-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 shadow-md">
            {activating
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Activating…</>
              : <><CheckCircle2 className="w-4 h-4" /> Activate Institution</>
            }
          </Button>
        )}
        {isActive && (
          <div className="space-y-2" data-testid="active-confirmation">
            <div className="flex items-center gap-2 text-emerald-700 font-medium text-sm">
              <CheckCircle2 className="w-5 h-5" /> Institution is ACTIVE and live.
            </div>
            {activationResult?.modules_provisioned > 0 && (
              <div
                className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg"
                data-testid="modules-provisioned-count"
              >
                <CheckCircle2 className="w-4 h-4 text-blue-600" />
                <span>
                  <strong>{activationResult.modules_provisioned}</strong> modules provisioned
                  {activationResult.edition_code && ` (${activationResult.edition_code} edition)`}
                </span>
              </div>
            )}
            {activationResult != null && activationResult.modules_provisioned === 0 && (
              <p className="text-xs text-slate-500" data-testid="no-modules-note">
                No package selected — assign subscription via subscription management.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main wizard
// ─────────────────────────────────────────────────────────────────────────────

export default function InstitutionOnboarding() {
  const { user } = useAuth();

  // Route guard — super_admin only
  if (!user) return null;
  if (user.role !== "super_admin") return <Navigate to="/dashboard" replace />;

  return <OnboardingWorkspace />;
}

function OnboardingWorkspace() {
  const [records, setRecords]         = useState([]);
  const [selected, setSelected]       = useState(null);  // full record obj
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving]           = useState(false);
  const [validating, setValidating]   = useState(false);
  const [activating, setActivating]   = useState(false);
  const [creating, setCreating]       = useState(false);
  const [activationResult, setActivationResult] = useState(null);

  // SUB-01-E: SaaS package loading
  const [packages, setPackages]         = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesError, setPackagesError]     = useState(null);

  // Step-local form state (separate from saved record to allow editing before save)
  const [localForm, setLocalForm]     = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // ── Load list ─────────────────────────────────────────────────────────────

  const loadRecords = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding/institutions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` },
      });
      if (res.ok) {
        const d = await res.json();
        setRecords(d.records || []);
      }
    } catch (e) {
      console.error("Failed to load onboarding records", e);
    }
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  // SUB-01-E: Load packages from /api/saas/packages
  useEffect(() => {
    setPackagesLoading(true);
    fetch(`${API_BASE}/api/saas/packages`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` },
    })
      .then(r => r.json())
      .then(d => setPackages(d.packages || []))
      .catch(e => setPackagesError("Failed to load packages: " + e.message))
      .finally(() => setPackagesLoading(false));
  }, []);

  // ── Select a record ───────────────────────────────────────────────────────

  const selectRecord = (rec) => {
    setSelected(rec);
    setCurrentStep(1);
    setLocalForm({});
    setFieldErrors({});
  };

  // ── Create DRAFT ──────────────────────────────────────────────────────────

  const createDraft = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding/institutions`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const rec = await res.json();
      await loadRecords();
      selectRecord(rec);
      toast.success("Onboarding draft created");
    } catch (e) {
      toast.error("Failed to create draft: " + e.message);
    } finally {
      setCreating(false);
    }
  };

  // ── Delete DRAFT ──────────────────────────────────────────────────────────

  const deleteDraft = async (oid) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding/institutions/${oid}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` },
      });
      if (!res.ok && res.status !== 204) throw new Error(await res.text());
      if (selected?.onboarding_id === oid) setSelected(null);
      await loadRecords();
      toast.success("Draft deleted");
    } catch (e) {
      toast.error("Cannot delete: " + e.message);
    }
  };

  // ── Get step key ──────────────────────────────────────────────────────────

  const stepKey = STEPS[currentStep - 1].key;

  // ── Initialise local form when step changes ───────────────────────────────

  useEffect(() => {
    if (!selected) return;
    if (stepKey === "identity")     setLocalForm({ ...(selected.step_identity     || {}) });
    if (stepKey === "settings")     setLocalForm({ ...(selected.step_settings     || {}) });
    if (stepKey === "calendar")     setLocalForm({ ...(selected.step_calendar     || {}) });
    if (stepKey === "admin")        setLocalForm({ ...(selected.step_admin        || {}), _password: "" });
    if (stepKey === "integrations") setLocalForm({ ...(selected.step_integrations || {}) });
    if (stepKey === "package")      setLocalForm({ ...(selected.step_package      || {}), package_code: selected.step_package?.package_code || "", enrolled_student_count: selected.step_package?.enrolled_student_count || 0 });
    if (stepKey === "review")       setLocalForm({});
    setFieldErrors({});
  }, [currentStep, selected?.onboarding_id]); // eslint-disable-line

  // ── Field change ──────────────────────────────────────────────────────────

  const handleChange = (key, val) => {
    setLocalForm(f => ({ ...f, [key]: val }));
    setFieldErrors(e => ({ ...e, [key]: null }));
  };

  // ── Save step ─────────────────────────────────────────────────────────────

  const saveStep = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      let body = { ...localForm };

      // Admin step: only include password if the user typed one
      if (stepKey === "admin") {
        const pw = body._password;
        delete body._password;
        if (pw) body.initial_password = pw;
        else if (!selected.step_admin?.password_set) {
          setFieldErrors({ initial_password: "Password is required for a new administrator" });
          setSaving(false);
          return;
        }
        // If no new password and one already set, skip the field entirely
        if (!pw && selected.step_admin?.password_set) {
          delete body.initial_password;
          // If nothing else changed, skip the PUT
          if (!body.name && !body.email && !body.title) {
            setSaving(false);
            setCurrentStep(s => Math.min(7, s + 1));
            return;
          }
        }
      }

      const res = await fetch(
        `${API_BASE}/api/admin/onboarding/institutions/${selected.onboarding_id}/steps/${stepKey}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("academiaos_token")}`,
          },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        let errData;
        try { errData = await res.json(); } catch { errData = { detail: await res.text() }; }
        const msg = typeof errData.detail === "string" ? errData.detail : JSON.stringify(errData.detail);
        toast.error(msg);
        return;
      }
      const updated = await res.json();
      setSelected(updated);
      setRecords(rs => rs.map(r => r.onboarding_id === updated.onboarding_id ? updated : r));
      setCurrentStep(s => Math.min(7, s + 1));
    } catch (e) {
      toast.error("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Validate ──────────────────────────────────────────────────────────────

  const runValidation = async () => {
    if (!selected) return;
    setValidating(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/onboarding/institutions/${selected.onboarding_id}/validate`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` },
        }
      );
      const d = await res.json();
      setSelected(prev => ({ ...prev, ...d, steps_complete: prev.steps_complete, step_identity: prev.step_identity, step_settings: prev.step_settings, step_calendar: prev.step_calendar, step_admin: prev.step_admin, step_integrations: prev.step_integrations }));
      // Reload full record
      const full = await fetch(
        `${API_BASE}/api/admin/onboarding/institutions/${selected.onboarding_id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` } }
      );
      if (full.ok) {
        const rec = await full.json();
        setSelected(rec);
        setRecords(rs => rs.map(r => r.onboarding_id === rec.onboarding_id ? rec : r));
      }
      if (d.ready_to_activate) toast.success("Validation passed — ready to activate");
      else toast.warning(`Validation: ${d.validation_errors?.length || 0} error(s) found`);
    } catch (e) {
      toast.error("Validation failed: " + e.message);
    } finally {
      setValidating(false);
    }
  };

  // ── Activate ──────────────────────────────────────────────────────────────

  const activate = async () => {
    if (!selected) return;
    setActivating(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/onboarding/institutions/${selected.onboarding_id}/activate`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` },
        }
      );
      const d = await res.json();
      if (!res.ok) {
        const msg = typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail);
        toast.error("Activation failed: " + msg);
        return;
      }
      // SUB-02: Store provisioning result for display
      setActivationResult({
        modules_provisioned: d.modules_provisioned || 0,
        edition_code: d.edition_code || null,
      });
      // Reload full record
      const full = await fetch(
        `${API_BASE}/api/admin/onboarding/institutions/${selected.onboarding_id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` } }
      );
      if (full.ok) {
        const rec = await full.json();
        setSelected(rec);
        setRecords(rs => rs.map(r => r.onboarding_id === rec.onboarding_id ? rec : r));
      }
      toast.success(`${d.message || "Institution activated successfully."}`);
    } catch (e) {
      toast.error("Activation error: " + e.message);
    } finally {
      setActivating(false);
    }
  };

  // ── Disabled state ────────────────────────────────────────────────────────

  const isLocked = selected?.status === "ACTIVE";

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <PageHeader
        title="Institution Onboarding"
        subtitle="Provision new institutions through a governed configuration wizard"
        data-testid="institution-onboarding-header"
      />

      <div className="grid grid-cols-12 gap-6">
        {/* ── Sidebar: Record list ───────────────────────────────────────── */}
        <aside className="col-span-3 space-y-3">
          <Button size="sm" onClick={createDraft} disabled={creating}
                  data-testid="create-draft-btn"
                  className="w-full gap-2 bg-slate-900 text-white hover:bg-slate-800">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            New Institution
          </Button>

          <div className="space-y-1.5" data-testid="onboarding-records-list">
            {records.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded-lg">
                No onboarding records yet
              </p>
            )}
            {records.map(rec => (
              <div
                key={rec.onboarding_id}
                data-testid={`record-item-${rec.onboarding_id}`}
                onClick={() => selectRecord(rec)}
                className={`group flex items-center justify-between px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                  selected?.onboarding_id === rec.onboarding_id
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate ${selected?.onboarding_id === rec.onboarding_id ? "text-white" : "text-slate-800"}`}>
                    {rec.step_identity?.name || "Unnamed Draft"}
                  </p>
                  <p className={`text-xs mt-0.5 ${selected?.onboarding_id === rec.onboarding_id ? "text-slate-300" : "text-slate-400"}`}>
                    {rec.step_identity?.short_code || "—"}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                    selected?.onboarding_id === rec.onboarding_id
                      ? "bg-white/20 text-white border-white/30"
                      : STATUS_COLORS[rec.status] || ""
                  }`}>
                    {rec.status === "VALIDATION_REQUIRED" ? "INVALID" : rec.status}
                  </span>
                  {(rec.status === "DRAFT" || rec.status === "VALIDATION_REQUIRED") && (
                    <button
                      data-testid={`delete-btn-${rec.onboarding_id}`}
                      onClick={e => { e.stopPropagation(); deleteDraft(rec.onboarding_id); }}
                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* ── Main: Wizard ───────────────────────────────────────────────── */}
        <main className="col-span-9">
          {!selected ? (
            <div className="flex flex-col items-center justify-center h-80 border border-dashed border-slate-200 rounded-xl text-center space-y-3">
              <Building2 className="w-10 h-10 text-slate-300" />
              <p className="text-slate-500 font-medium">Select a record or create a new institution</p>
              <p className="text-sm text-slate-400">Each institution is fully isolated — no shared tenant data.</p>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
              {/* Step navigator */}
              <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-0"
                   data-testid="step-navigator">
                {STEPS.map((step, idx) => {
                  const complete = selected.steps_complete?.[step.key] || step.key === "review";
                  const active   = currentStep === step.id;
                  const Icon     = step.icon;
                  return (
                    <React.Fragment key={step.id}>
                      <button
                        data-testid={`step-btn-${step.key}`}
                        onClick={() => step.id <= currentStep && setCurrentStep(step.id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                          active
                            ? "bg-slate-900 text-white"
                            : complete
                              ? "text-emerald-600 hover:bg-emerald-50"
                              : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        {complete && !active
                          ? <CheckCircle2 className="w-4 h-4" />
                          : <Icon className="w-4 h-4" />
                        }
                        <span className="hidden xl:inline">{step.label}</span>
                      </button>
                      {idx < STEPS.length - 1 && (
                        <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Active institution banner */}
              {isLocked && (
                <div className="mx-6 mt-4 flex items-center gap-3 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800"
                     data-testid="active-institution-banner">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Institution is ACTIVE.</span>
                    {" "}{selected.step_identity?.name} has been provisioned. Configuration is locked.
                    {selected.activation_result?.admin_email && (
                      <span className="ml-1 text-green-700">
                        Administrator: {selected.activation_result.admin_email}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Step content */}
              <div className="px-6 py-6">
                <div className="mb-5">
                  <h2 className="text-base font-semibold text-slate-900" data-testid="step-title">
                    Step {currentStep} — {STEPS[currentStep - 1].label}
                  </h2>
                </div>

                {stepKey === "identity" && (
                  <StepIdentity data={localForm} onChange={handleChange}
                                errors={fieldErrors} disabled={isLocked} />
                )}
                {stepKey === "settings" && (
                  <StepSettings data={localForm} onChange={handleChange}
                                errors={fieldErrors} disabled={isLocked} />
                )}
                {stepKey === "calendar" && (
                  <StepCalendar data={localForm} onChange={handleChange}
                                errors={fieldErrors} disabled={isLocked} />
                )}
                {stepKey === "admin" && (
                  <StepAdmin data={localForm} onChange={handleChange}
                             errors={fieldErrors} disabled={isLocked}
                             isNew={!selected.step_admin?.password_set} />
                )}
                {stepKey === "integrations" && (
                  <StepIntegrations data={selected.step_integrations || {}} />
                )}
                {stepKey === "package" && (
                  <StepPackage
                    data={localForm}
                    onChange={d => setLocalForm(d)}
                    loading={packagesLoading}
                    packages={packages}
                    error={packagesError}
                  />
                )}
                {stepKey === "review" && (
                  <StepReview
                    record={selected}
                    onValidate={runValidation}
                    onActivate={activate}
                    validating={validating}
                    activating={activating}
                    activationResult={activationResult}
                  />
                )}
              </div>

              {/* Navigation footer */}
              {stepKey !== "review" && (
                <div className="border-t border-slate-100 px-6 py-4 flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => setCurrentStep(s => Math.max(1, s - 1))}
                          disabled={currentStep === 1} data-testid="prev-step-btn" className="gap-1.5">
                    <ChevronLeft className="w-4 h-4" /> Previous
                  </Button>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{currentStep} / {STEPS.length}</span>
                    {isLocked ? (
                      <Button size="sm" onClick={() => setCurrentStep(s => Math.min(7, s + 1))}
                              data-testid="next-step-btn" className="gap-1.5">
                        Next <ChevronRight className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button size="sm" onClick={saveStep} disabled={saving}
                              data-testid="save-continue-btn" className="gap-1.5 bg-slate-900 text-white hover:bg-slate-800">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Save & Continue <ChevronRight className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
